/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import useOnclickOutside from 'react-cool-onclickoutside';
import { createPortal } from 'react-dom';
import Iframe from '../../Util/tiptap/iframe.ts';
import { generateEmbedUrlFromVideoUrl } from '../../Util/data';

const levels = [
  {
    value: 0,
    title: 'Normal'
  },
  {
    value: 1,
    title: 'Heading 1'
  },
  {
    value: 2,
    title: 'Heading 2'
  },
  {
    value: 3,
    title: 'Heading 3'
  },
  {
    value: 4,
    title: 'Heading 4'
  },
  {
    value: 5,
    title: 'Heading 5'
  },
  {
    value: 6,
    title: 'Heading 6'
  }
];

const slugify = (text) => {
  return String(text || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const addIdsToHeadings = (html) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const used = new Set();
    const headings = doc.querySelectorAll('h1,h2,h3,h4,h5');
    headings.forEach((h) => {
      let id = h.getAttribute('id');
      if (!id || id.trim() === '') {
        const base = slugify(h.textContent);
        let unique = base || 'heading';
        let i = 2;
        while (used.has(unique) || doc.getElementById(unique)) {
          unique = base ? `${base}-${i++}` : `heading-${i++}`;
        }
        h.setAttribute('id', unique);
        used.add(unique);
      } else {
        used.add(id);
      }
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const Tiptap = ({ content, updateContent, contentClassName = '' }) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [showImageUrlInput, setShowImageUrlInput] = useState(false);
  const [previousUrl, setPreviousUrl] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [textSizeValue, setTextSizeValue] = useState('Normal');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showVideoUrlInput, setShowVideoUrlInput] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const levelSelection = useOnclickOutside(() => {
    setDropdownOpen(false);
  });

  const editor = useEditor({
    content,
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: null, rel: null }
      }),
      Image.configure({
        inline: true,
        allowBase64: false
      }),
      Iframe
    ],
    editorProps: {
      attributes: {
        class: `border border-gray-300 focus:border-blue-600 focus:outline-solid outline-blue-600 rounded-sm w-full p-3 flex flex-col gap-2 rounded-t-none max-h-96 overflow-auto focus:outline-hidden custom-thumb ${contentClassName}`,
        testid: 'editor-content'
      }
    },
    onUpdate: () => {
      const html = editor.getHTML();
      const htmlWithIds = addIdsToHeadings(html);
      updateContent(htmlWithIds);
    }
  });

  const updateUrl = (url) => {
    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    // update link
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({
        href: url,
        target: openInNewTab ? '_blank' : null,
        rel: openInNewTab ? 'noreferrer noopener' : null
      })
      .run();
  };

  const handleUrlInput = (e) => {
    setPreviousUrl(e.target.value);
  };

  const toggleLink = () => {
    setPreviousUrl(editor.getAttributes('link').href);
    const currentTarget = editor.getAttributes('link').target || null;
    setOpenInNewTab(currentTarget === '_blank');
    setShowLinkModal(true);
  };

  const updateSelectedUrl = (url) => {
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({
        href: url,
        target: openInNewTab ? '_blank' : null,
        rel: openInNewTab ? 'noreferrer noopener' : null
      })
      .run();
    setShowLinkModal(false);
  };

  const toggleImageUrlInput = () => {
    setShowImageUrlInput(true);
    // eslint-disable-next-line no-alert
    const url = window.prompt('Image URL', previousUrl);
    if (url) {
      addImage(url);
    }
  };

  const addImage = (url) => {
    editor.chain().focus().setImage({ src: url }).run();
    setShowImageUrlInput(false);
  };

  const updateHeading = (heading) => {
    if (heading.value === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: heading.value }).run();
    }
    setTextSizeValue(heading.title);
    setDropdownOpen(false);
  };

  useEffect(() => {
    const currentUrl = editor.getAttributes('link').href || '';
    setPreviousUrl(currentUrl);
    const currentTarget = editor.getAttributes('link').target || null;
    setOpenInNewTab(currentTarget === '_blank');
  }, [editor?.state.selection]);

  useEffect(() => {
    if (editor) {
      editor.commands.focus('end');
    }
  }, []);

  const handleToggleBlockquote = () => {
    if (!editor) return;
    if (editor.isActive('blockquote')) {
      editor.chain().focus().setParagraph().run();
      editor.chain().focus().lift().run();
    } else {
      editor.chain().focus().setParagraph().run();
      editor.chain().focus().toggleBlockquote().run();
    }
  };

  return (
    <div className="w-full" tabIndex={-1}>
      <div className="w-full flex flex-wrap gap-2 py-2 rounded-t border border-b-0 border-gray-300 px-3">
        <div
          className="relative inline-flex items-center justify-center"
          ref={levelSelection}
        >
          <button
            type="button"
            className="inline-flex items-center gap-2 hover:bg-gray-200 px-2 rounded-sm"
            onClick={() => {
              return setDropdownOpen((value) => {
                return !value;
              });
            }}
          >
            {textSizeValue}
            <span className="material-symbols-outlined text-xl text-gray-700">
              expand_all
            </span>
          </button>
          {isDropdownOpen && (
            <div
              ref={levelSelection}
              className="z-10 absolute top-9 left-0 w-36 py-2.5 bg-white rounded-xl shadow-xl border border-gray-200"
            >
              <ul className="list-none list-inside w-full pl-0">
                {levels.map((item) => {
                  return (
                    <li key={item.value} className="w-full">
                      <button
                        type="button"
                        onClick={() => {
                          return updateHeading(item);
                        }}
                        className="px-4 py-2 hover:bg-gray-200 w-full block text-left"
                      >
                        {item.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            return editor.chain().focus().toggleBold().run();
          }}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('bold') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            format_bold
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            return editor.chain().focus().toggleItalic().run();
          }}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('italic') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            format_italic
          </span>
        </button>
        <button
          type="button"
          onClick={handleToggleBlockquote}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('blockquote') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            {editor?.isActive('blockquote')
              ? 'format_quote_off'
              : 'format_quote'}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            return editor.chain().focus().toggleOrderedList().run();
          }}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('orderedList') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-icons text-xl text-gray-700">
            format_list_numbered
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            return editor.chain().focus().toggleBulletList().run();
          }}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('bulletList') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-icons text-xl text-gray-700">
            format_list_bulleted
          </span>
        </button>
        <button
          type="button"
          onClick={toggleLink}
          className={`inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded ${
            editor?.isActive('link') ? 'bg-gray-200' : ''
          }`}
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            {!editor?.isActive('link') ? 'link' : 'link_off'}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            return setShowImageUrlInput(true);
          }}
          className="inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded"
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            image
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            return setShowVideoUrlInput(true);
          }}
          className="inline-flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded"
        >
          <span className="material-symbols-outlined text-xl text-gray-700">
            videocam
          </span>
        </button>
      </div>

      {showImageUrlInput &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="p-4 rounded-lg bg-white shadow-xl flex flex-col w-full max-w-md gap-3 border border-gray-200">
              <div className="text-gray-800 font-semibold">Enter Image URL</div>
              <input
                type="url"
                required
                className="block px-3 py-2 w-full input--brand font-normal"
                placeholder="https://www.example.com/image.jpg"
                onInput={(e) => {
                  editor
                    .chain()
                    .focus()
                    .setImage({ src: e.target.value })
                    .run();
                  setShowImageUrlInput(false);
                }}
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  className="rounded-full bg-gray-200 text-gray-800 py-1.5 px-3 text-sm"
                  onClick={() => setShowImageUrlInput(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showVideoUrlInput &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="p-4 rounded-lg bg-white shadow-xl flex flex-col w-full max-w-md gap-3 border border-gray-200">
              <div className="text-gray-800 font-semibold">
                Enter Video URL (YouTube or Vimeo only)
              </div>
              <input
                type="url"
                required
                className="block px-3 py-2 w-full input--brand font-normal"
                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                onInput={(e) => setVideoUrl(e.target.value)}
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  className="rounded-full bg-gray-200 text-gray-800 py-1.5 px-3 text-sm"
                  onClick={() => setShowVideoUrlInput(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-full bg-gray-800 text-white py-1.5 px-3 text-sm"
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .setIframe({
                        src: generateEmbedUrlFromVideoUrl(videoUrl)
                      })
                      .run();
                    setShowVideoUrlInput(false);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showLinkModal &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="p-4 rounded-lg bg-white shadow-xl flex flex-col w-full max-w-md gap-3 border border-gray-200">
              <div className="text-gray-800 font-semibold">Add / Edit Link</div>
              <input
                type="url"
                required
                className="block px-3 py-2 w-full input--brand font-normal"
                placeholder="https://example.com"
                value={previousUrl}
                onInput={(e) => setPreviousUrl(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="bg-custom-checkbox bg-size-[100%_100%] bg-center bg-no-repeat shrink-0 appearance-none cursor-pointer w-4 h-4 rounded-sm border border-gray-400 bg-white checked:bg-blue-600 checked:border-transparent focus:outline-hidden"
                  checked={openInNewTab}
                  onChange={(e) => setOpenInNewTab(e.target.checked)}
                />
                Open link in new tab
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  className="rounded-full bg-gray-200 text-gray-800 py-1.5 px-3 text-sm"
                  onClick={() => setShowLinkModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-full bg-gray-800 text-white py-1.5 px-3 text-sm"
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .extendMarkRange('link')
                      .setLink({
                        href: previousUrl,
                        target: openInNewTab ? '_blank' : null,
                        rel: openInNewTab ? 'noreferrer noopener' : null
                      })
                      .run();
                    setShowLinkModal(false);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <EditorContent
        className="editable-content prose max-w-none"
        editor={editor}
      />
    </div>
  );
};

export default Tiptap;

Tiptap.propTypes = {
  content: PropTypes.string.isRequired,
  updateContent: PropTypes.func.isRequired,
  contentClassName: PropTypes.string
};
